import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { MessageCircle, Send, Trash2, User } from "lucide-react";

const AVATAR_COLORS = [
    "bg-vibrantorange", "bg-vibrantyellow", "bg-vibrantblue", "bg-vibrantpink",
];

const POLL_MS = 4000;
const MAX_LEN = 300;

export default function PageChat({ room, title = "Chat" }) {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const listRef = useRef(null);
    const isLoggedIn = user && user !== false;

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/chat/${room}/messages`);
            setMessages(data);
        } catch {
            /* ignore */
        }
    }, [room]);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages]);

    const onSend = async (e) => {
        e.preventDefault();
        const text = draft.trim();
        if (!text || sending) return;
        setSending(true);
        setError("");
        try {
            const { data } = await api.post(`/chat/${room}/messages`, { text });
            setMessages((prev) => [...prev, data]);
            setDraft("");
        } catch (err) {
            setError(err?.response?.data?.detail || "Couldn't send");
        } finally {
            setSending(false);
        }
    };

    const onDelete = async (id) => {
        try {
            await api.delete(`/chat/messages/${id}`);
            setMessages((prev) => prev.filter((m) => m.id !== id));
        } catch {
            /* ignore */
        }
    };

    return (
        <div data-testid={`chat-${room}`} className="brutal-card p-5 bg-white">
            <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-vibrantblue border-[3px] border-ink rounded-xl flex items-center justify-center shadow-brutal">
                    <MessageCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-heading font-black text-lg text-ink tracking-tight">
                    {title} <span className="text-ink/40 font-bold text-sm">({messages.length})</span>
                </h3>
            </div>

            <div
                ref={listRef}
                data-testid={`chat-${room}-list`}
                className="space-y-3 max-h-72 overflow-y-auto pr-1 mb-4"
            >
                {messages.length === 0 ? (
                    <p data-testid={`chat-${room}-empty`} className="font-body text-ink/50 text-sm text-center py-4">
                        No messages yet. Say something!
                    </p>
                ) : (
                    messages.map((m, idx) => {
                        const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                        const isMine = isLoggedIn && user.id === m.user_id;
                        return (
                            <div key={m.id} data-testid={`chat-msg-${m.id}`} className="flex gap-2.5">
                                <div className={`flex-shrink-0 w-8 h-8 ${color} border-[3px] border-ink rounded-full flex items-center justify-center`}>
                                    <User className="w-3.5 h-3.5 text-ink" strokeWidth={3} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="bg-cream border-[3px] border-ink rounded-2xl rounded-tl-sm px-3 py-2 shadow-brutal">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-heading font-bold text-xs text-ink">@{m.username}</span>
                                            {isMine && (
                                                <button
                                                    onClick={() => onDelete(m.id)}
                                                    data-testid={`chat-msg-delete-${m.id}`}
                                                    className="text-ink/40 hover:text-vibrantpink transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="font-body text-sm text-ink/90 mt-0.5 whitespace-pre-line break-words">
                                            {m.text}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {isLoggedIn ? (
                <form onSubmit={onSend} data-testid={`chat-${room}-form`}>
                    <textarea
                        data-testid={`chat-${room}-input`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                onSend(e);
                            }
                        }}
                        placeholder="Send a message…"
                        rows={2}
                        maxLength={MAX_LEN}
                        className="brutal-input resize-none text-sm"
                    />
                    {error && (
                        <p data-testid={`chat-${room}-error`} className="mt-1.5 font-body text-xs text-vibrantpink font-semibold">
                            {error}
                        </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                        <span className="font-body text-[10px] text-ink/40">
                            {draft.length}/{MAX_LEN}
                        </span>
                        <button
                            type="submit"
                            disabled={sending || !draft.trim()}
                            className="brutal-btn-primary py-1.5 px-3 text-sm"
                            data-testid={`chat-${room}-send`}
                        >
                            <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                            {sending ? "Sending…" : "Send"}
                        </button>
                    </div>
                </form>
            ) : (
                <div data-testid={`chat-${room}-login-gate`} className="text-center py-3 bg-cream border-[3px] border-dashed border-ink/20 rounded-xl">
                    <p className="font-body text-xs text-ink/60">
                        <Link to="/login" className="font-heading font-bold underline decoration-vibrantorange decoration-[2px] underline-offset-2">
                            Log in
                        </Link>{" "}
                        to join the chat
                    </p>
                </div>
            )}
        </div>
    );
}
